import { ReviewPanel, AgentEmptyState } from '../ReviewPanel';
import type { AgentOutputRow, ArtifactRow } from '@/hooks/useJobProgress';

interface MosaicPayload {
  name: string;
  bytes?: number;
  width?: number | null;
  height?: number | null;
}

interface MosaicPanelProps {
  outputs: AgentOutputRow[];
  artifacts: ArtifactRow[];
  isRunning?: boolean;
}

export function MosaicPanel({ outputs, artifacts, isRunning }: MosaicPanelProps) {
  if (outputs.length === 0) {
    return (
      <ReviewPanel agent="mosaic" index={6}>
        <AgentEmptyState agent="mosaic" isRunning={isRunning} />
      </ReviewPanel>
    );
  }

  const artByName = new Map<string, ArtifactRow>();
  for (const a of artifacts) {
    if (a.kind === 'asset_image' && a.ref) artByName.set(a.ref, a);
  }

  return (
    <ReviewPanel
      agent="mosaic"
      index={6}
      subtitle="Generated assets"
      rightTag={`${outputs.length} asset${outputs.length > 1 ? 's' : ''}`}
    >
      <p className="text-body text-graphite mb-4">
        Logos, hero illustrations, icons, and covers generated for the project. These are referenced
        by FORGE in the deployable code.
      </p>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {outputs.map((o) => {
          const payload = o.payload as MosaicPayload;
          const art = artByName.get(payload.name);
          return (
            <figure key={o._id} className="space-y-1.5">
              <div className="aspect-square overflow-hidden rounded-sm border border-ink/10 bg-paper-2 grid place-items-center">
                {art?.url ? (
                  <img src={art.url} alt={payload.name} className="max-h-full max-w-full" />
                ) : (
                  <span className="text-graphite text-[12px]">Generating…</span>
                )}
              </div>
              <figcaption className="font-mono text-[11px] text-ink truncate">{payload.name}</figcaption>
              {payload.width && payload.height && (
                <p className="font-mono text-[10px] text-graphite">{payload.width}×{payload.height}</p>
              )}
            </figure>
          );
        })}
      </div>
    </ReviewPanel>
  );
}
